// ============================================================
// SignatureUploadCard.tsx — B3b: a user's OWN signature image, printed above
// the typed signature line on every bill / credit note they sign.
//   • My settings → "Bill signature" (your own: /api/me/signature).
//   • Admin › Team → each member's "Bill signature" row (userId given:
//     /api/users/:id/signature — admins only, server-enforced).
// The avatar presigned-PUT flow: request URL → PUT straight to R2 → confirm.
// PNG / JPG up to 2 MB (the server re-checks both). The image is shown on
// white, as it prints. Saved straight away; every change is audit-logged
// server-side. A bill already issued keeps the signature it was issued with.
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import { SettingsCard, SettingsRow, RowSmall, S_BTN_GHOST, S_BTN_GHOST_SM, S_NOTE } from './settingsUi'
import { confirmSignatureApi, getUserSignatureApi, removeSignatureApi, requestSignatureUploadUrlApi } from '../../api/casevoApi'
import type { UserSignature } from '../../types/billing.types'
import { serverErrorMessage } from '../../utils/serverError'

export const MAX_SIGNATURE_BYTES = 2 * 1024 * 1024
export const SIGNATURE_TIP = 'PNG or JPG, up to 2 MB. Sign in dark ink on white paper (or use a transparent background) and crop close to the signature.'
// Pure (exported for tests): why a chosen file can't be used, or null.
export const signatureFileProblem = (file: { type: string; size: number }): string | null => {
  if (file.type !== 'image/png' && file.type !== 'image/jpeg') return 'Choose a PNG or JPG image.'
  if (file.size > MAX_SIGNATURE_BYTES) return 'That image is too large. The limit is 2 MB.'
  return null
}

interface Props {
  // Absent = your own signature (My settings). Given = a team member's (admins).
  userId?: string
  // The member's name, for labels when an admin manages it.
  name?: string
  // 'card' (My settings) or 'row' (Admin › Team, inside the member's card).
  variant?: 'card' | 'row'
  disabled?: boolean
}

const SignatureUploadCard: React.FC<Props> = ({ userId, name, variant = 'card', disabled }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [sig, setSig] = useState<UserSignature | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const errId = `sig-err-${userId ?? 'me'}`

  useEffect(() => {
    let live = true
    getUserSignatureApi(userId)
      .then((s) => { if (live) setSig(s) })
      .catch((e) => { if (live) setLoadErr(serverErrorMessage(e) ?? 'Couldn’t load the signature.') })
    return () => { live = false }
  }, [userId])

  const onFile = async (file: File | undefined): Promise<void> => {
    if (inputRef.current) inputRef.current.value = ''
    if (!file) return
    const problem = signatureFileProblem(file)
    if (problem) { setErr(problem); setSaved(null); return }
    setBusy(true); setErr(null); setSaved(null)
    try {
      const { uploadUrl, r2Key } = await requestSignatureUploadUrlApi({ mimeType: file.type, sizeBytes: file.size }, userId)
      // Direct PUT to R2 — ONLY Content-Type (an auth header would break the
      // presigned signature). Same as the avatar uploader.
      const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file })
      if (!put.ok) throw new Error('The upload didn’t finish. Please try again.')
      setSig(await confirmSignatureApi(r2Key, userId))
      setSaved('Signature saved.')
    } catch (e) {
      setErr(serverErrorMessage(e) ?? (e instanceof Error ? e.message : 'Couldn’t upload the signature. Please try again.'))
    } finally { setBusy(false) }
  }
  const remove = async (): Promise<void> => {
    setBusy(true); setErr(null); setSaved(null)
    try { setSig(await removeSignatureApi(userId)); setSaved('Signature removed.') }
    catch (e) { setErr(serverErrorMessage(e) ?? 'Couldn’t remove the signature.') } finally { setBusy(false) }
  }

  const whose = userId ? `${name ?? 'this member'}’s` : 'your'
  const preview = (
    <div className="w-[220px] h-[72px] rounded-[10px] border border-casevo-border bg-white grid place-items-center overflow-hidden shrink-0">
      {sig?.signatureUrl
        ? <img src={sig.signatureUrl} alt={`${userId ? `${name ?? 'Member'}’s` : 'Your'} signature`} className="max-w-[200px] max-h-[60px] object-contain" />
        : <span className="text-[12px] text-[#55635c] px-3 text-center">{sig ? 'No signature image' : 'Loading…'}</span>}
    </div>
  )
  const buttons = (
    <div className="flex flex-wrap gap-[8px]">
      <input ref={inputRef} type="file" accept="image/png,image/jpeg" className="hidden" aria-label={`Upload ${whose} signature image`} onChange={(e) => { void onFile(e.target.files?.[0]) }} />
      <button type="button" className={variant === 'row' ? S_BTN_GHOST_SM : S_BTN_GHOST} disabled={busy || disabled || !sig}
        aria-describedby={err ? errId : undefined} onClick={() => inputRef.current?.click()}>
        {busy ? 'Working…' : sig?.hasSignature ? 'Replace image' : 'Upload image'}
      </button>
      {sig?.hasSignature && <button type="button" className={`${variant === 'row' ? S_BTN_GHOST_SM : S_BTN_GHOST} !text-casevo-danger`} disabled={busy || disabled} onClick={() => { void remove() }}>Remove</button>}
    </div>
  )
  const message = loadErr
    ? <span id={errId} role="alert" className="text-casevo-danger">{loadErr}</span>
    : err
      ? <span id={errId} role="alert" className="text-casevo-danger">{err}</span>
      : saved
        ? <span role="status">{saved} Bills already issued keep the signature they were issued with.</span>
        : <>{SIGNATURE_TIP} Without an image, bills print the typed signature line only.</>

  if (variant === 'row') {
    return (
      <SettingsRow label="Bill signature" action={buttons}>
        {preview}
        <RowSmall>{message}</RowSmall>
      </SettingsRow>
    )
  }
  return (
    <SettingsCard icon="pen" title="Bill signature" sub={`Printed above the typed signature line on bills and credit notes ${userId ? 'they sign' : 'you sign'}.`}>
      <div className="flex flex-wrap items-center gap-[14px] pt-[6px]">{preview}{buttons}</div>
      <p className={`${S_NOTE} mt-[8px] mb-0`}>{message}</p>
    </SettingsCard>
  )
}

export default SignatureUploadCard
